/**
 * GlobalSearch - Site-wide search component for the website
 */
import React, { useState, useCallback, useEffect, useRef } from "react";
import { makeStyles } from "@mui/styles";
import { TextField, Typography, CircularProgress, InputAdornment, Modal, Box } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { changeTab } from "../redux/TabSlice";
import { setSelectedItem } from "../redux/OrganizationSlice";
import { setSelectedGuide, getSelectedGuidance } from "../redux/OffenceSlice";
import axios from "axios";
import Header from "../components/Header";
import Options from "../components/Options";
import ModalAbout from "../components/ModalAbout";
import ArrowBack from "../assets/img/HumQadamHome/arrowBack.svg";
import { trackSearch, trackPageView } from "../helpers/api";
import { getTranslation, getTitleFromAppContent } from "../helpers/utils";

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: "#F6F1F4",
    minHeight: "100vh",
  },
  searchContainer: {
    padding: "20px",
    maxWidth: "600px",
    margin: "0 auto",
  },
  searchField: {
    backgroundColor: "#fff",
    borderRadius: "25px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "25px",
    },
  },
  resultsContainer: {
    marginTop: "20px",
  },
  sectionTitle: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
    marginTop: "20px",
    marginBottom: "10px",
  },
  resultItem: {
    backgroundColor: "#fff",
    padding: "15px",
    marginBottom: "10px",
    borderRadius: "10px",
    cursor: "pointer",
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
  },
  resultTitle: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  resultType: {
    fontSize: "0.75rem",
    color: "#666",
    textTransform: "uppercase",
  },
  noResults: {
    textAlign: "center",
    color: "#666",
    marginTop: "40px",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    marginTop: "40px",
  },
}));

function GlobalSearch() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openAbout, setOpenAbout] = useState(false);
  const [pageModalContent, setPageModalContent] = useState(null);
  const [searchError, setSearchError] = useState(null);
  
  const { langId, selLanguageId } = useSelector((state) => state.language);
  const { selProvinceId } = useSelector((state) => state.province);
  const { selDistrictId } = useSelector((state) => state.district);
  const appContent = useSelector((state) => state.appContent?.appContent) ?? [];

  const t = (name, fallback) => getTitleFromAppContent(appContent, name, langId) || fallback || name;
  const labelOrganizations = t("Organizations", "Organizations");
  const labelOrganization = t("Organization", "Organization");
  const labelGuidanceTopics = t("Guidance Topics", "Guidance Topics");
  const labelGuidance = t("Guidance", "Guidance");
  const labelLaws = t("Laws", "Laws");
  const labelLaw = t("Law", "Law");
  const labelGuidanceSteps = t("Guidance Steps", "Guidance Steps");
  const labelGuidanceStep = t("Guidance Step", "Guidance Step");
  const labelFrom = t("From", "From");
  const labelGuidanceDetails = t("Guidance Details", "Guidance Details");
  const labelGuidanceDetail = t("Guidance Detail", "Guidance Detail");
  const labelPage = t("Page", "Page");
  const labelPages = t("Pages", "Pages");
  const placeholderSearch = t("Search placeholder", "Search organizations, laws, guidance...");
  const msgNoResults = t("No results found. Try a different search term.", "No results found. Try a different search term.");
  const msgSearchFailed = t("Search failed. Please try again.", "Search failed. Please try again.");
  const promptSearch = t("Start typing to search across organizations, laws, guidance, and more", "Start typing to search across organizations, laws, guidance, and more");

  useEffect(() => {
    trackPageView("Global Search", { province: selProvinceId, district: selDistrictId, language: selLanguageId });
  }, [selProvinceId, selDistrictId, selLanguageId]);

  // Track search only once per query
  const lastTrackedSearch = useRef("");

  // Helper function to safely extract text from Translation objects/arrays
  // Always returns a string, never an object
  const getTextFromTranslation = (translation, fallbackLangId = langId) => {
    try {
      if (!translation) return '';
      
      // If it's already a string, return it
      if (typeof translation === 'string') return translation;
      
      // If it's an array of Translation objects
      if (Array.isArray(translation)) {
        const result = getTranslation(translation, fallbackLangId);
        if (result && typeof result === 'string') return result;
        // Fallback: get first text if available
        if (translation.length > 0) {
          const firstItem = translation[0];
          if (firstItem && typeof firstItem === 'object') {
            const text = firstItem.text;
            if (text && typeof text === 'string') return text;
            // If text is an object, try to get its text property
            if (text && typeof text === 'object' && text.text && typeof text.text === 'string') {
              return text.text;
            }
          }
        }
        return '';
      }
      
      // If it's a single Translation object with text property
      if (typeof translation === 'object' && translation !== null) {
        // Direct text property
        if (translation.text) {
          if (typeof translation.text === 'string') return translation.text;
          // If text is an object, try to get its text property
          if (typeof translation.text === 'object' && translation.text.text && typeof translation.text.text === 'string') {
            return translation.text.text;
          }
        }
      }
    } catch (e) {
      console.error('Error extracting text from translation:', e, translation);
    }
    
    return '';
  };

  // Auto-focus search input when component mounts
  const searchInputRef = useRef(null);
  useEffect(() => {
    // Focus the search input when page loads
    if (searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, []);

  const performSearch = useCallback(
    async (query) => {
      if (!query || query.length < 2) {
        setResults(null);
        return;
      }

      setLoading(true);
      setSearchError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_LOCAL_API}/search`,
          { params: { q: query, language: langId } }
        );
        const contentType = response.headers?.["content-type"] || "";
        if (!contentType.includes("application/json")) {
          setSearchError(true);
          setResults(null);
          return;
        }
        // API returns { success: true, data: { results: {...} } }
        setResults(response.data?.data?.results || response.data?.data);
        if (!response.data?.success && response.status >= 400) {
          setSearchError(true);
          setResults(null);
          return;
        }
        // Track search event (only if query changed)
        if (query !== lastTrackedSearch.current) {
          lastTrackedSearch.current = query;
          trackSearch(query, { province: selProvinceId, district: selDistrictId, language: langId });
        }
      } catch (error) {
        console.error("Search error:", error);
        setSearchError(true);
        setResults(null);
      } finally {
        setLoading(false);
      }
    },
    [langId, selDistrictId, selProvinceId]
  );

  // Debounced search - only if searchText has at least 2 characters
  useEffect(() => {
    if (!searchText || searchText.trim().length < 2) {
      setResults(null);
      setLoading(false);
      setSearchError(null);
      return;
    }
    
    const timer = setTimeout(() => {
      performSearch(searchText.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText, performSearch]);

  const handleResultClick = (item, type) => {
    switch (type) {
      case "organization":
        dispatch(setSelectedItem(item));
        dispatch(changeTab("HelplineDetails"));
        break;
      case "offence":
        // Check if item has full data (guidance_steps), if not fetch it
        if (item._id && (!item.guidance_steps || item.guidance_steps.length === 0)) {
          // Fetch full offence data with all guidance steps
          if (selDistrictId) {
            dispatch(getSelectedGuidance({ 
              id: item._id, 
              district: selDistrictId, 
              langId: selLanguageId 
            }));
          } else {
            // If no district, just set the item (will show loading)
            dispatch(setSelectedGuide(item));
          }
        } else {
          // Item already has full data, use it directly
          dispatch(setSelectedGuide(item));
        }
        dispatch(changeTab("GuideStep"));
        break;
      case "law":
        // Search returns Law documents (_id, title, website_link). Open link when present; backend /laws/:id expects offence id, not law id.
        if (item.website_link) {
          window.open(item.website_link, "_blank");
        }
        break;
      case "guidance_step":
        // Navigate to the parent offence's guidance step page
        if (item.parent_offence_id) {
          // Always fetch full data for the parent offence
          if (selDistrictId) {
            dispatch(getSelectedGuidance({ 
              id: item.parent_offence_id, 
              district: selDistrictId, 
              langId: selLanguageId 
            }));
          } else {
            dispatch(setSelectedGuide({ _id: item.parent_offence_id }));
          }
          dispatch(changeTab("GuideStep"));
        }
        break;
      case "guidance_step_description":
        // Navigate to the parent offence's guidance step page
        if (item.parent_offence_id) {
          // Always fetch full data for the parent offence
          if (selDistrictId) {
            dispatch(getSelectedGuidance({ 
              id: item.parent_offence_id, 
              district: selDistrictId, 
              langId: selLanguageId 
            }));
          } else {
            dispatch(setSelectedGuide({ _id: item.parent_offence_id }));
          }
          dispatch(changeTab("GuideStep"));
        }
        break;
      case "page":
        setPageModalContent({
          title: getTextFromTranslation(item?.title),
          description: getTextFromTranslation(item?.description),
        });
        break;
      default:
        break;
    }
  };

  const renderResults = () => {
    if (!results) return null;

    const hasResults =
      results.organizations?.length > 0 ||
      results.offences?.length > 0 ||
      results.laws?.length > 0 ||
      results.pages?.length > 0 ||
      results.guidance_steps?.length > 0 ||
      results.guidance_step_descriptions?.length > 0;

    if (!hasResults) {
      return (
        <Typography className={classes.noResults}>
          {msgNoResults}
        </Typography>
      );
    }

    return (
      <div className={classes.resultsContainer}>
        {/* Organizations */}
        {results.organizations?.length > 0 && (
          <>
            <Typography className={classes.sectionTitle}>
              {labelOrganizations}
            </Typography>
            {results.organizations.map((org) => (
              <div
                key={org._id}
                className={classes.resultItem}
                onClick={() => handleResultClick(org, "organization")}
              >
                <Typography className={classes.resultType}>
                  {labelOrganization}
                </Typography>
                <Typography className={classes.resultTitle}>
                  {String(getTextFromTranslation(org.title) || '')}
                </Typography>
              </div>
            ))}
          </>
        )}

        {/* Offences/Guidance */}
        {results.offences?.length > 0 && (
          <>
            <Typography className={classes.sectionTitle}>
              {labelGuidanceTopics}
            </Typography>
            {results.offences.map((offence) => (
              <div
                key={offence._id}
                className={classes.resultItem}
                onClick={() => handleResultClick(offence, "offence")}
              >
                <Typography className={classes.resultType}>
                  {labelGuidance}
                </Typography>
                <Typography className={classes.resultTitle}>
                  {String(getTextFromTranslation(offence.title) || '')}
                </Typography>
              </div>
            ))}
          </>
        )}

        {/* Laws */}
        {results.laws?.length > 0 && (
          <>
            <Typography className={classes.sectionTitle}>
              {labelLaws}
            </Typography>
            {results.laws.map((law) => (
              <div
                key={law._id}
                className={classes.resultItem}
                onClick={() => handleResultClick(law, "law")}
              >
                <Typography className={classes.resultType}>
                  {labelLaw}
                </Typography>
                <Typography className={classes.resultTitle}>
                  {String(getTextFromTranslation(law.title) || '')}
                </Typography>
              </div>
            ))}
          </>
        )}

        {/* Guidance Steps */}
        {results.guidance_steps?.length > 0 && (
          <>
            <Typography className={classes.sectionTitle}>
              {labelGuidanceSteps}
            </Typography>
            {results.guidance_steps.map((step) => (
              <div
                key={step._id}
                className={classes.resultItem}
                onClick={() => handleResultClick(step, "guidance_step")}
              >
                <Typography className={classes.resultType}>
                  {labelGuidanceStep}
                </Typography>
                <Typography className={classes.resultTitle}>
                  {String(getTextFromTranslation(step.title) || '')}
                </Typography>
                {step.parent_offence_title && (
                  <Typography className={classes.resultSubtitle} style={{ fontSize: '0.875rem', color: '#666', marginTop: '4px' }}>
                    {labelFrom}{labelFrom && !labelFrom.endsWith(':') ? ': ' : ' '}
                    {String(getTextFromTranslation(step.parent_offence_title) || '')}
                  </Typography>
                )}
              </div>
            ))}
          </>
        )}

        {/* Guidance Step Descriptions */}
        {results.guidance_step_descriptions?.length > 0 && (
          <>
            <Typography className={classes.sectionTitle}>
              {labelGuidanceDetails}
            </Typography>
            {results.guidance_step_descriptions.map((desc) => (
              <div
                key={desc._id}
                className={classes.resultItem}
                onClick={() => handleResultClick(desc, "guidance_step_description")}
              >
                <Typography className={classes.resultType}>
                  {labelGuidanceDetail}
                </Typography>
                <Typography className={classes.resultTitle}>
                  {String(getTextFromTranslation(desc.heading) || getTextFromTranslation(desc.description) || '')}
                </Typography>
                {desc.parent_offence_title && (
                  <Typography className={classes.resultSubtitle} style={{ fontSize: '0.875rem', color: '#666', marginTop: '4px' }}>
                    {labelFrom}{labelFrom && !labelFrom.endsWith(':') ? ': ' : ' '}
                    {String(getTextFromTranslation(desc.parent_offence_title) || '')}
                  </Typography>
                )}
              </div>
            ))}
          </>
        )}

        {/* Pages / Content */}
        {results.pages?.length > 0 && (
          <>
            <Typography className={classes.sectionTitle}>
              {labelPages}
            </Typography>
            {results.pages.map((page) => (
              <div
                key={page._id}
                className={classes.resultItem}
                onClick={() => handleResultClick(page, "page")}
              >
                <Typography className={classes.resultType}>
                  {labelPage}
                </Typography>
                <Typography className={classes.resultTitle}>
                  {String(getTextFromTranslation(page?.title) || '')}
                </Typography>
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

  return (
    <div className={classes.container}>
      <Header />
      <Options handleOpen={() => setOpenAbout(true)} />
      
      <div className={classes.searchContainer}>
        <TextField
          inputRef={searchInputRef}
          fullWidth
          variant="outlined"
          placeholder={placeholderSearch}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : searchError ? (
          <Typography className={classes.noResults}>
            {msgSearchFailed}
          </Typography>
        ) : searchText.trim().length >= 2 ? (
          renderResults()
        ) : (
          <Typography className={classes.promptText} style={{ textAlign: 'center', marginTop: '40px', color: '#666' }}>
            {promptSearch}
          </Typography>
        )}
      </div>

      <img
        alt="Arrow Back"
        onClick={() => dispatch(changeTab("HumQadam"))}
        src={ArrowBack}
        className="mt-8 cursor-pointer ml-5"
      />

      <ModalAbout open={openAbout} handleClose={() => setOpenAbout(false)} />

      <Modal open={Boolean(pageModalContent)} onClose={() => setPageModalContent(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#F6F1F4",
            p: 3,
            width: "100%",
            maxWidth: "min(500px, calc(100vw - 32px))",
            maxHeight: "80vh",
            overflow: "auto",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" className={classes.resultTitle} style={{ marginBottom: 16 }}>
            {pageModalContent?.title || ""}
          </Typography>
          <Typography variant="body1" style={{ color: "#333", whiteSpace: "pre-wrap" }}>
            {pageModalContent?.description || ""}
          </Typography>
          <Typography
            component="button"
            onClick={() => setPageModalContent(null)}
            style={{ marginTop: 16, cursor: "pointer", color: "inherit", textDecoration: "underline" }}
          >
            Close
          </Typography>
        </Box>
      </Modal>
    </div>
  );
}

export default GlobalSearch;
